import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const fmtInt = (n) => (Number(n) || 0).toLocaleString();

const StatCard = ({ label, value, sub }) => (
  <div className="stat-card">
    <div className="stat-label">{label}</div>
    <div className="stat-value" style={{ fontSize: '28px' }}>{value}</div>
    {sub != null && <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px' }}>{sub}</div>}
  </div>
);

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState(null);
  const { fetchWithAuth } = useAuth();
  const limit = 20;

  // Debounce typing: one request per pause instead of one per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    (async () => {
      setLoading(true);
      try {
        const response = await fetchWithAuth(
          `/api/users?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`,
          { signal: controller.signal }
        );
        if (!response.ok) throw new Error('Failed to fetch users');
        const data = await response.json();
        if (cancelled) return;
        setUsers(data.data);
        setTotal(data.total);
        setError(null);
      } catch (err) {
        if (!cancelled && err.name !== 'AbortError') setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    // Aborting the in-flight request on change means a slow old response can
    // never overwrite a newer one.
    return () => { cancelled = true; controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search]);

  useEffect(() => {
    (async () => {
      try {
        const resp = await fetchWithAuth('/api/users/stats');
        if (resp.ok) setStats(await resp.json());
      } catch (_) {
        // stats are non-critical — the table still works without them
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  if (loading && users.length === 0 && !search) return <div className="loading">Loading...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  return (
    <div>
      <h1 style={{ fontSize: '32px', fontWeight: '700', marginBottom: '24px' }}>Users</h1>

      {stats && (
        <div className="grid grid-4" style={{ marginBottom: 20 }}>
          <StatCard
            label="Total users"
            value={fmtInt(stats.total)}
            sub={`${fmtInt(stats.active)} active`}
          />
          <StatCard
            label="Registered"
            value={fmtInt(stats.registered)}
            sub={`${Math.round((Number(stats.registered) / Math.max(1, Number(stats.total))) * 100)}% of total · ${fmtInt(stats.anonymous)} anonymous`}
          />
          <StatCard
            label="New (7 days)"
            value={fmtInt(stats.new7d)}
            sub={`+${fmtInt(stats.newToday)} in last 24h`}
          />
          <StatCard
            label="New (30 days)"
            value={fmtInt(stats.new30d)}
            sub={`~${fmtInt(Math.round(Number(stats.new30d) / 30))} per day`}
          />
        </div>
      )}

      <div className="card">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search by name, email, username or ID..."
            className="input"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <span style={{ color: '#666', fontSize: '14px', alignSelf: 'center' }}>
            {loading ? 'Searching…' : search ? `${fmtInt(total)} found` : `${fmtInt(total)} total users`}
          </span>
        </div>

        <div className="table-container" style={{ opacity: loading ? 0.6 : 1 }}>
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Gender</th>
                <th>Birthdate</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>{user.id}</td>
                  <td>{user.name || user.surname ? `${user.name || ''} ${user.surname || ''}`.trim() : 'Anonymous'}</td>
                  <td>{user.email || 'N/A'}</td>
                  <td>{user.gender === 1 ? 'Male' : user.gender === 2 ? 'Female' : 'N/A'}</td>
                  <td>{user.birthdate || 'N/A'}</td>
                  <td>
                    {user.active ? (
                      <span className="badge badge-success">Active</span>
                    ) : (
                      <span className="badge badge-warning">Inactive</span>
                    )}
                  </td>
                  <td>{formatDate(user.created_at)}</td>
                  <td>
                    <Link to={`/users/${user.id}`} className="link">
                      View Details
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && users.length === 0 && (
            <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}>
              Nothing found for «{search}». Try another name, email or ID.
            </div>
          )}
        </div>

        <div className="pagination">
          <button
            className="btn btn-secondary"
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
          >
            Previous
          </button>
          <span className="page-info">
            Page {page} of {totalPages}
          </span>
          <button
            className="btn btn-secondary"
            onClick={() => setPage(page + 1)}
            disabled={page >= totalPages}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default Users;
